import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { colors, radius } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import ErrorBanner from '../../components/ErrorBanner';
import { searchMatch } from '../../utils/searchMatch';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Marka Atanmamış Reçeteler — Faz 2 worklist.
 * marka_id IS NULL olan AKTIF reçeteler; kullanıcı satır-içi marka atar (PUT /recipes/{id}).
 * Backend: GET /api/recipes/marka-atanmamis (VIEW_RECIPES). Atama MANAGE_RECIPES ister.
 * Reçete create artık zorunlu marka olduğundan bu liste yalnız legacy kayıtları içerir;
 * boşaldığında iş tamamdır (Faz 3 ops: NOT NULL değerlendirmesi).
 */
const MarkaAtanmamisPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_RECIPES);

  const [data, setData] = useState(null);
  const [markalar, setMarkalar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [arama, setArama] = useState('');
  const [secim, setSecim] = useState({});   // recipe_id -> marka_id (seçili)
  const [atanan, setAtanan] = useState(null); // o an atanıyor olan recipe_id

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/recipes/marka-atanmamis`, { headers: getAuthHeaders() });
      if (res.status === 401) { window.location.href = '/login'; return; }
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const err = await res.json(); if (err && err.detail) detail = err.detail; } catch (_) { /* JSON değil */ }
        throw new Error(detail);
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message || 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetch(`${API_URL}/api/markalar`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setMarkalar(Array.isArray(d) ? d : (d.markalar || [])))
      .catch(() => setMarkalar([]));
  }, [fetchData]);

  const handleAta = async (recipeId) => {
    const markaId = secim[recipeId];
    if (!markaId) { toast.error('Önce bir işletme (marka) seçin'); return; }
    setAtanan(recipeId);
    try {
      const res = await fetch(`${API_URL}/api/recipes/${recipeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ marka_id: parseInt(markaId, 10) }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const err = await res.json(); if (err && err.detail) detail = err.detail; } catch (_) { /* JSON değil */ }
        throw new Error(detail);
      }
      toast.success('İşletme atandı');
      // Satır listeden düşsün (refetch yerine yerel filtre — anlık geri bildirim)
      setData(prev => prev ? {
        receteler: prev.receteler.filter(r => r.id !== recipeId),
        toplam: Math.max(0, (prev.toplam || 0) - 1),
      } : prev);
    } catch (err) {
      toast.error(err.message || 'Atama başarısız');
    } finally {
      setAtanan(null);
    }
  };

  if (loading && !data) return <div style={{ padding: 24 }}>Yükleniyor…</div>;

  const satirlar = (data?.receteler || []).filter(
    r => searchMatch(`${r.ad} ${r.recete_kodu} ${r.marka_legacy || ''}`, arama)
  );

  return (
    <div>
      {error && <div style={{ padding: '0 20px' }}><ErrorBanner error={error} onRetry={fetchData} /></div>}

      <div style={{ padding: '12px 20px 0', fontSize: '12px', color: colors.textSecondary, lineHeight: 1.5 }}>
        ℹ️ İşletme (marka) atanmamış AKTIF reçeteler. Her reçete bir işletmeye ait olmalı —
        satır-içi seçip "Ata" deyin. Yeni reçetelerde marka zorunludur; bu liste yalnız
        eski kayıtları içerir, boşaldığında iş tamamdır.
      </div>

      <div style={{ display: 'flex', gap: '8px', padding: '12px 20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{
          display: 'inline-block', padding: '5px 12px', borderRadius: radius.lg, fontSize: '13px',
          fontWeight: 'bold', background: (data?.toplam ?? 0) > 0 ? '#fff8e1' : '#e8f5e9',
          color: (data?.toplam ?? 0) > 0 ? '#b45309' : '#2e7d32',
        }}>
          {(data?.toplam ?? 0) > 0 ? `${data.toplam} reçete marka bekliyor` : 'Tüm reçetelere marka atanmış 🎉'}
        </span>
        <input
          placeholder="Ara (ad, kod...)"
          value={arama}
          onChange={e => setArama(e.target.value)}
          style={{ flex: 1, minWidth: '200px', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
        />
      </div>

      <div style={{ padding: '0 20px', overflowX: 'auto' }}>
        {satirlar.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            {(data?.toplam ?? 0) === 0 ? 'Atanmamış reçete yok.' : 'Aramaya uygun kayıt yok.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '7px', textAlign: 'left' }}>Reçete</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Tip</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Eski Marka (ipucu)</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>İşletme Ata</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {satirlar.map(r => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${colors.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '10px' }}>
                    <div style={{ fontWeight: 'bold' }}>{r.ad}</div>
                    <div style={{ fontSize: '11px', color: colors.textTertiary, fontFamily: 'monospace' }}>{r.recete_kodu}</div>
                  </td>
                  <td style={{ padding: '7px', textAlign: 'center', color: colors.textSecondary, fontSize: '12px' }}>{r.tip}</td>
                  <td style={{ padding: '7px', color: colors.textTertiary, fontSize: '12px' }}>{r.marka_legacy || '—'}</td>
                  <td style={{ padding: '7px' }}>
                    <select
                      value={secim[r.id] || ''}
                      disabled={!canManage || atanan === r.id}
                      onChange={e => setSecim(s => ({ ...s, [r.id]: e.target.value }))}
                      style={{ padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', minWidth: '160px' }}>
                      <option value="">Seçiniz…</option>
                      {markalar.map(m => <option key={m.id} value={m.id}>{m.ad}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '7px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {canManage && (
                      <button onClick={() => handleAta(r.id)} disabled={!secim[r.id] || atanan === r.id} title="İşletme ata"
                        style={{ background: '#e8f5e9', border: 'none', borderRadius: radius.sm, padding: '5px 12px', cursor: (!secim[r.id] || atanan === r.id) ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold', opacity: (!secim[r.id] || atanan === r.id) ? 0.5 : 1, marginRight: '6px' }}>
                        {atanan === r.id ? '…' : '✓ Ata'}
                      </button>
                    )}
                    <button onClick={() => navigate(`/recete/duzenle?id=${r.id}`)} title="Düzenle"
                      style={{ background: '#e3f2fd', border: 'none', borderRadius: radius.sm, padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      ✎ Düzenle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MarkaAtanmamisPage;
